import type { Component } from 'vue'
import { markRaw } from 'vue'

type ModalPayload = unknown
type ModalClosedCallback = (event: ModalPayload) => void

export type ModalType = 'modal' | 'panel' | 'side'

export interface ModalOptions {
  width?: number
  padding?: number
  closable?: boolean
  background?: string
  type?: ModalType
  blur?: boolean
}

export interface ModalConfigs {
  options?: ModalOptions
  props?: Record<string, unknown>
  onClosed?: ModalClosedCallback | null
}

export interface ModalEntry {
  component: Component
  props: Record<string, unknown>
  options: ModalOptions
  onClosed: ModalClosedCallback | null
  closed: boolean
}

const defaultConfigs = (): Required<ModalConfigs> => ({
  options: {},
  props: {},
  onClosed: null,
})

export const useModals = () => {
  return useState<ModalEntry[]>('modals', () => [])
}

export const VueModally = () => {
  const modals = useModals()

  const setModalCloseState = (index: number) => {
    modals.value[index].closed = true
  }

  const fireClosedCallback = (index: number, event: ModalPayload) => {
    const closedCallback = modals.value[index]?.onClosed
    if (closedCallback) {
      closedCallback(event)
    }
  }

  const closeLastModal = () => {
    if (modals.value.length > 0) {
      setModalCloseState(modals.value.length - 1)
      modals.value.pop()
    }
  }

  const addNewModal = (component: Component, configs: ModalConfigs = {}) => {
    const { options, props } = { ...defaultConfigs(), ...configs }
    const modal: ModalEntry = {
      component,
      props,
      options,
      onClosed: configs.onClosed ?? null,
      closed: false,
    }

    closeLastModal()
    modals.value.push(modal)
    return modals.value.length - 1
  }

  const openModal = (
    component: Component,
    configs: ModalConfigs = defaultConfigs(),
    onClosed: ModalClosedCallback | null = null,
  ) => {
    const { options, props } = { ...defaultConfigs(), ...configs }

    addNewModal(component, {
      props,
      options,
      onClosed,
    })
  }

  const closeAllModal = (event: ModalPayload) => {
    const total = modals.value.length
    if (total === 1) {
      setModalCloseState(0)
      fireClosedCallback(0, event)
      modals.value.pop()
      return
    }

    for (let index = total - 1; index >= 0; index -= 1) {
      setModalCloseState(index)
      fireClosedCallback(index, event)
      modals.value.pop()
    }
  }

  return {
    modals,
    openModal,
    addNewModal,
    closeAllModal,
    setModalCloseState,
    fireClosedCallback,
    closeLastModal,
  }
}

export const useModal = (
  component: Component,
  configs: ModalConfigs = defaultConfigs(),
) => {
  return new Promise<ModalPayload>((resolve) => {
    const { openModal } = VueModally()

    openModal(markRaw(component), configs, (event) => {
      resolve(event)
    })
  })
}

export const closeModal = (index: number, event: ModalPayload = null) => {
  const { modals, setModalCloseState, fireClosedCallback } = VueModally()
  setModalCloseState(index)
  fireClosedCallback(index, event)
  if (index > -1) modals.value.splice(index, 1)
}
